<template>
  <div class="sols">
    <!-- ============ 首屏 ============ -->
    <section class="sols-hero">
      <div class="sols-hero-bg">
        <img :src="img('gen/hero-home.jpg')" alt="皓辰科技解决方案与功能域"/>
        <div class="sols-hero-mask"></div>
        <TechCanvas class="sols-canvas" color="34, 224, 255" link-color="34, 224, 255"/>
        <div class="grid-bg"></div>
      </div>

      <div class="page sols-hero-inner">
        <nav class="crumb">
          <router-link to="/home">首页</router-link>
          <span>/</span>
          <em>解决方案</em>
        </nav>
        <span class="eyebrow reveal" v-reveal>Solutions</span>
        <h1 class="h1 reveal" v-reveal="{ delay: 80 }">
          解决方案与功能域
          <span class="sols-tagline">把能力拆到看得见、可验收的粒度</span>
        </h1>
        <p class="sols-summary reveal" v-reveal="{ delay: 160 }">
          下面每一页对应一个可以独立交付、单独验收的功能域，写清了它包含哪些能力、
          怎么运转、以及哪些边界必须现场确认——不用听概念，直接对着自己现场逐条核对。
        </p>
      </div>
    </section>

    <!-- ============ 按业务线分组 ============ -->
    <section
      v-for="(group, gi) in groups"
      :key="group.line.key"
      class="sec"
      :class="{ 'sec-tint': gi % 2 === 1 }"
    >
      <div class="page">
        <SectionHeading
          :eyebrow="group.line.en"
          :title="`${group.line.name} · 功能域`"
          :desc="group.desc"
        />
        <div class="sol-grid">
          <router-link
            v-for="(s, i) in group.items"
            :key="s.slug"
            :to="`/solutions/${s.slug}`"
            class="sol-card reveal"
            v-reveal="{ delay: (i % 3) * 90 }"
            :style="{ '--accent': group.line.accent }"
          >
            <h3>{{ s.name }}</h3>
            <p class="sol-tag">{{ s.tagline }}</p>
            <p class="sol-sum">{{ s.summary }}</p>
            <span class="sol-link">查看能力详情 <IconSvg name="arrow-right"/></span>
          </router-link>
        </div>
        <div class="sol-more">
          <router-link :to="group.line.path" class="btn btn-ghost">
            查看「{{ group.line.name }}」业务线总览
            <IconSvg name="arrow-right"/>
          </router-link>
        </div>
      </div>
    </section>

    <!-- ============ 怎么选 ============ -->
    <section class="sec">
      <div class="page">
        <SectionHeading
          eyebrow="How to start"
          title="功能域这么多，从哪个开始"
          desc="不建议一次全上。按下面的顺序判断，投入可控，也更容易拿到阶段性结果。"
        />
        <ol class="pick">
          <li class="pick-item reveal" v-reveal>
            <span class="pick-num">01</span>
            <h3>先定位最痛的一个环节</h3>
            <p>是数据看不见、控制靠人跑、记录查不到，还是出了问题追不回？先认定一个，别铺开。</p>
          </li>
          <li class="pick-item reveal" v-reveal="{ delay: 90 }">
            <span class="pick-num">02</span>
            <h3>确认前置条件在不在</h3>
            <p>监测类看点位与网络，控制类看设备是否可控，追溯类看记录能不能在发生时录入。</p>
          </li>
          <li class="pick-item reveal" v-reveal="{ delay: 180 }">
            <span class="pick-num">03</span>
            <h3>先跑一个小范围闭环</h3>
            <p>一个棚、一条产线、一个批次走通完整链路，比全量铺开更能暴露真实问题。</p>
          </li>
        </ol>
      </div>
    </section>

    <CtaBand
      eyebrow="Next Step"
      title="不确定该从哪个功能域开始？"
      desc="把现场情况讲清楚，皓辰给出可落地的范围、周期与投入建议。"
      :bg="img('gen/cta-bg.jpg')"
    />
  </div>
</template>

<script>
import TechCanvas from '@/components/common/TechCanvas'
import IconSvg from '@/components/common/IconSvg'
import SectionHeading from '@/components/common/SectionHeading'
import CtaBand from '@/components/common/CtaBand'
import img from '@/utils/assets'
import { businessLines } from '@/data/site'
import { solutions } from '@/data/solutions'

const GROUP_DESC = {
  agriculture: '从设备接入、环境监测到水肥灌溉与溯源存证，覆盖大田、设施、畜牧、水产四类业态。',
  mes: '承接上层生产订单，把车间执行落到工单、工序、批次与质量记录上。',
  iot: '协议解析、设备接入、状态监测与远程控制，面向产线与公用工程现场。',
  enterprise: '数字化咨询、定制软件开发与系统集成，从需求梳理到上线运维全程负责。'
}

export default {
  name: 'SolutionsIndex',
  components: { TechCanvas, IconSvg, SectionHeading, CtaBand },
  data() {
    return { img }
  },
  computed: {
    /** 按业务线分组，只显示确有对应解决方案的业务线 */
    groups() {
      return businessLines
        .map((line) => ({
          line,
          desc: GROUP_DESC[line.key] || '',
          items: solutions.filter((s) => s.line === line.key)
        }))
        .filter((g) => g.items.length > 0)
    }
  }
}
</script>

<style lang="scss" scoped>
.sols { position: relative; }

.sols-hero {
  position: relative;
  min-height: 66vh;
  display: flex;
  align-items: center;
  padding: calc(var(--nav-h) + 46px) 0 78px;
  overflow: hidden;
}

.sols-hero-bg {
  position: absolute;
  inset: 0;

  img { width: 100%; height: 100%; object-fit: cover; opacity: 0.44; }

  .sols-hero-mask {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 66% 60% at 20% 40%, rgba(34, 224, 255, 0.2), transparent 68%),
      linear-gradient(180deg, rgba(3, 7, 17, 0.9), rgba(3, 7, 17, 0.76) 45%, rgba(3, 7, 17, 0.98));
  }

  .sols-canvas { opacity: 0.55; }
}

.sols-hero-inner { position: relative; z-index: 2; max-width: 880px; }

.crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 26px;
  font-size: 13px;
  color: var(--txt-3);

  a { display: inline-block; padding: 7px 2px; }
  a:hover { color: var(--accent-txt); }
  em { font-style: normal; color: var(--accent-txt); }
}

.sols-tagline {
  display: block;
  margin-top: 14px;
  font-size: clamp(19px, 2.3vw, 30px);
  font-weight: 700;
  background: linear-gradient(100deg, #22e0ff, #24e39a);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.sols-summary {
  margin-top: 24px;
  max-width: 760px;
  font-size: 16.5px;
  line-height: 1.95;
  color: var(--txt-2);
}

/* ---------------- 卡片 ---------------- */
.sol-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px; }

.sol-card {
  display: flex;
  flex-direction: column;
  padding: 28px 28px 26px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, rgba(13, 27, 54, 0.78), rgba(6, 13, 29, 0.72));
  transition: transform 0.5s, border-color 0.5s, box-shadow 0.5s;

  h3 { font-size: 19px; color: #fff; }
  .sol-tag {
    margin-top: 10px;
    font-size: 13.5px;
    font-weight: 700;
    color: var(--accent);
  }
  .sol-sum {
    margin-top: 14px;
    font-size: 13.8px;
    line-height: 1.85;
    color: var(--txt-3);
    flex: 1;
  }
  .sol-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 20px;
    padding-top: 18px;
    border-top: 1px solid rgba(120, 176, 255, 0.1);
    font-size: 14px;
    color: var(--accent-txt);
    svg { width: 17px; height: 17px; transition: transform 0.4s; }
  }

  &:hover { transform: translateY(-8px); border-color: color-mix(in srgb, var(--accent) 45%, transparent); box-shadow: 0 30px 66px -36px var(--accent); }
  &:hover .sol-link svg { transform: translateX(5px); }
}

.sol-more { margin-top: 30px; }

/* ---------------- 从哪开始 ---------------- */
.pick { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px; }

.pick-item {
  position: relative;
  padding: 30px 28px;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, rgba(11, 23, 48, 0.68), rgba(5, 12, 27, 0.52));
  transition: transform 0.45s, border-color 0.45s;

  &:hover { transform: translateY(-6px); border-color: var(--line-strong); }

  .pick-num {
    display: inline-block;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.12em;
    color: var(--accent-txt);
  }

  h3 { margin-top: 14px; font-size: 17px; color: #fff; }
  p { margin-top: 10px; font-size: 14px; line-height: 1.85; color: var(--txt-3); }
}

/* ---------------- 响应式 ---------------- */
@media (max-width: 1080px) {
  .sol-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .pick { grid-template-columns: minmax(0, 1fr); }
}

@media (max-width: 760px) {
  .sols-hero { min-height: auto; padding: calc(var(--nav-h) + 30px) 0 56px; }
  .sols-summary { font-size: 15px; }
  .sol-grid { grid-template-columns: minmax(0, 1fr); gap: 16px; }
  .sol-card { padding: 24px 22px; }
}
</style>
